import * as React from 'react'
import { useFocusable, FocusContext } from '@noriginmedia/norigin-spatial-navigation'

export interface FocusScopeProps {
  /** The overlay's content — the stops arrows should be confined to. */
  children: React.ReactNode;
  className?: string;
}

/**
 * A focus trap for overlays: arrows move freely between the stops inside, and
 * cannot leave.
 *
 * The counterpart to {@link Focusable}. A stop is a leaf; this is the container
 * that says "navigation stops here". Spatial navigation is geometric, and the app
 * is one flat focus tree — so an overlay that merely *covers* the page does not
 * hide it from the remote. The page's tiles and nav links are still mounted, still
 * have bounding boxes, and are still the nearest candidate in some direction. The
 * viewer arrows off the end of the overlay and the ring vanishes onto something
 * behind it, which they cannot see and did not ask for.
 *
 * `isFocusBoundary` is what prevents that: Norigin refuses to hand focus to a
 * candidate outside this subtree. The overlay must therefore contain its own way
 * out (a Close control, Escape) — a trap with no exit is a trap.
 *
 * IMPORTANT: mount this only while the overlay is open. Rendered around a single
 * collapsed control it would pin the ring to that control permanently. Render it
 * conditionally rather than toggling a prop, so the boundary exists exactly as
 * long as the thing it is trapping focus for.
 *
 * `saveLastFocusedChild` restores the viewer's place when focus re-enters, and
 * `trackChildren` is what lets the container know it has any.
 */
export function FocusScope ({ children, className }: FocusScopeProps) {
  const { ref, focusKey } = useFocusable({
    isFocusBoundary: true,
    trackChildren: true,
    saveLastFocusedChild: true,
  })

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className={className}>{children}</div>
    </FocusContext.Provider>
  )
}
